import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mail, 
  Calendar, 
  Newspaper, 
  RefreshCw, 
  Users, 
  MessageSquare, 
  Clock,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Lock,
  Eye,
  EyeOff
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  
  const [activeTab, setActiveTab] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});

  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Check if already authenticated
  useEffect(() => {
    const authToken = sessionStorage.getItem('adminAuth');
    if (authToken === 'authenticated') {
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch all data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setAuthError('');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });
      
      if (response.ok) {
        setIsAuthenticated(true);
        sessionStorage.setItem('adminAuth', 'authenticated');
      } else {
        setAuthError(t('adminWrongPassword'));
      }
    } catch (err) {
      setAuthError('Connection error. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuth');
    setPassword('');
  };

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [contactsRes, bookingsRes, subscribersRes] = await Promise.all([
        fetch(`${API_URL}/api/contact`),
        fetch(`${API_URL}/api/booking`),
        fetch(`${API_URL}/api/newsletter`)
      ]);

      if (!contactsRes.ok || !bookingsRes.ok || !subscribersRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const [contactsData, bookingsData, subscribersData] = await Promise.all([
        contactsRes.json(),
        bookingsRes.json(),
        subscribersRes.json()
      ]);

      // Sort by date (newest first)
      setContacts(contactsData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setBookings(bookingsData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setSubscribers(subscribersData.sort((a, b) => new Date(b.subscribed_at) - new Date(a.subscribed_at)));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const serviceNames = {
    video: 'Video Production',
    post: 'Post Production',
    marketing: 'Marketing & Branding',
    web: 'Web Services'
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0b004c] to-[#1a0066] flex items-center justify-center p-4">
        <SEO 
          title="Admin Dashboard - Datasignz CreativeMinds"
          description="Admin dashboard for managing form submissions"
          noIndex={true}
        />
        
        <div className="w-full max-w-md">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-montserrat-bold text-white mb-2">
                {t('adminLogin')}
              </h1>
              <p className="text-white/70 text-sm">
                {t('adminLoginDesc')}
              </p>
            </div>
            
            <form onSubmit={handleLogin}>
              <div className="mb-6">
                <label className="block text-white/80 text-sm mb-2">{t('adminPassword')}</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-white/50"
                    placeholder="Enter password"
                    data-testid="admin-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {authError && (
                  <p className="text-red-400 text-sm mt-2">{authError}</p>
                )}
              </div>
              
              <button
                type="submit"
                disabled={isLoggingIn}
                className="w-full py-3 bg-white text-[#0b004c] rounded-lg font-medium hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="admin-login-btn"
              >
                {isLoggingIn ? 'Logging in...' : t('adminLoginBtn')}
              </button>
            </form>
            
            <button
              onClick={() => navigate('/')}
              className="w-full mt-4 py-3 border border-white/30 text-white rounded-lg font-medium hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('adminBackToSite')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0b004c] to-[#1a0066]">
      <SEO 
        title="Admin Dashboard - Datasignz CreativeMinds"
        description="Admin dashboard for managing form submissions"
        noIndex={true}
      />
      
      {/* Header */}
      <header className="bg-[#0b004c] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="text-white/70 hover:text-white transition-colors"
              data-testid="admin-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-montserrat-bold text-white">{t('adminDashboard')}</h1>
          </div>
          
          <div className="flex items-center gap-4">
            <button
              onClick={fetchAllData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {t('adminRefresh')}
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors"
              data-testid="admin-logout-btn"
            >
              {t('adminLogout')}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div 
            className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 cursor-pointer hover:bg-white/15 transition-colors"
            onClick={() => setActiveTab('contacts')}
            data-testid="stats-contacts"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-white/60 text-sm">{t('adminContactSubmissions')}</p>
                <p className="text-3xl font-montserrat-bold text-white">{contacts.length}</p>
              </div>
            </div>
          </div>

          <div 
            className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 cursor-pointer hover:bg-white/15 transition-colors"
            onClick={() => setActiveTab('bookings')}
            data-testid="stats-bookings"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-white/60 text-sm">{t('adminBookingRequests')}</p>
                <p className="text-3xl font-montserrat-bold text-white">{bookings.length}</p>
              </div>
            </div>
          </div>

          <div 
            className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 cursor-pointer hover:bg-white/15 transition-colors"
            onClick={() => setActiveTab('subscribers')}
            data-testid="stats-subscribers"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Newspaper className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-white/60 text-sm">{t('adminNewsletterSubs')}</p>
                <p className="text-3xl font-montserrat-bold text-white">{subscribers.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-white/10 overflow-x-auto">
          <button
            onClick={() => setActiveTab('contacts')}
            className={`px-4 py-3 font-medium whitespace-nowrap transition-colors ${
              activeTab === 'contacts'
                ? 'text-white border-b-2 border-white'
                : 'text-white/60 hover:text-white'
            }`}
            data-testid="tab-contacts"
          >
            <Mail className="w-4 h-4 inline mr-2" />
            {t('adminContacts')}
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            className={`px-4 py-3 font-medium whitespace-nowrap transition-colors ${
              activeTab === 'bookings'
                ? 'text-white border-b-2 border-white'
                : 'text-white/60 hover:text-white'
            }`}
            data-testid="tab-bookings"
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            {t('adminBookings')}
          </button>
          <button
            onClick={() => setActiveTab('subscribers')}
            className={`px-4 py-3 font-medium whitespace-nowrap transition-colors ${
              activeTab === 'subscribers'
                ? 'text-white border-b-2 border-white'
                : 'text-white/60 hover:text-white'
            }`}
            data-testid="tab-subscribers"
          >
            <Users className="w-4 h-4 inline mr-2" />
            {t('adminSubscribers')}
          </button>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-6 text-red-300">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-white animate-spin" />
          </div>
        )}

        {/* Content */}
        {!loading && (
          <div className="space-y-4">
            {/* Contacts Tab */}
            {activeTab === 'contacts' && (
              contacts.length === 0 ? (
                <div className="text-center py-12 text-white/60">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t('adminNoContacts')}</p>
                </div>
              ) : (
                contacts.map((contact) => (
                  <div 
                    key={contact.id}
                    className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden"
                    data-testid={`contact-item-${contact.id}`}
                  >
                    <div 
                      className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
                      onClick={() => toggleExpand(contact.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                            <span className="text-blue-400 font-medium">
                              {contact.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <h3 className="text-white font-medium">{contact.name}</h3>
                            <p className="text-white/60 text-sm">{contact.subject}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-white/40 text-sm hidden sm:block">
                            <Clock className="w-4 h-4 inline mr-1" />
                            {formatDate(contact.created_at)}
                          </span>
                          {expandedItems[contact.id] ? (
                            <ChevronUp className="w-5 h-5 text-white/50" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-white/50" />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {expandedItems[contact.id] && (
                      <div className="px-4 pb-4 border-t border-white/10 pt-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-white/50 text-xs uppercase mb-1">{t('email')}</p>
                            <a href={`mailto:${contact.email}`} className="text-blue-400 hover:underline">
                              {contact.email}
                            </a>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs uppercase mb-1">{t('adminDate')}</p>
                            <p className="text-white">{formatDate(contact.created_at)}</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-white/50 text-xs uppercase mb-1">{t('message')}</p>
                          <p className="text-white whitespace-pre-wrap">{contact.message}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )
            )}

            {/* Bookings Tab */}
            {activeTab === 'bookings' && (
              bookings.length === 0 ? (
                <div className="text-center py-12 text-white/60">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t('adminNoBookings')}</p>
                </div>
              ) : (
                bookings.map((booking) => (
                  <div 
                    key={booking.id}
                    className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden"
                    data-testid={`booking-item-${booking.id}`}
                  >
                    <div 
                      className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
                      onClick={() => toggleExpand(booking.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-green-400" />
                          </div>
                          <div>
                            <h3 className="text-white font-medium">{booking.name}</h3>
                            <p className="text-white/60 text-sm">
                              {serviceNames[booking.service] || booking.service}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {booking.date && (
                            <span className="text-green-400 text-sm hidden sm:block">
                              {booking.date}
                            </span>
                          )}
                          {expandedItems[booking.id] ? (
                            <ChevronUp className="w-5 h-5 text-white/50" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-white/50" />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {expandedItems[booking.id] && (
                      <div className="px-4 pb-4 border-t border-white/10 pt-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <p className="text-white/50 text-xs uppercase mb-1">{t('email')}</p>
                            <a href={`mailto:${booking.email}`} className="text-blue-400 hover:underline">
                              {booking.email}
                            </a>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs uppercase mb-1">{t('phone')}</p>
                            <p className="text-white">{booking.phone || '-'}</p>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs uppercase mb-1">{t('preferredDate')}</p>
                            <p className="text-white">{booking.date || '-'}</p>
                          </div>
                        </div>
                        {booking.notes && (
                          <div className="mt-4">
                            <p className="text-white/50 text-xs uppercase mb-1">{t('additionalNotes')}</p>
                            <p className="text-white whitespace-pre-wrap">{booking.notes}</p>
                          </div>
                        )}
                        <div className="mt-4">
                          <p className="text-white/50 text-xs uppercase mb-1">{t('adminSubmittedAt')}</p>
                          <p className="text-white">{formatDate(booking.created_at)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )
            )}

            {/* Subscribers Tab */}
            {activeTab === 'subscribers' && (
              subscribers.length === 0 ? (
                <div className="text-center py-12 text-white/60">
                  <Newspaper className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t('adminNoSubscribers')}</p>
                </div>
              ) : (
                <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left p-4 text-white/60 text-sm font-medium">{t('email')}</th>
                        <th className="text-left p-4 text-white/60 text-sm font-medium hidden sm:table-cell">{t('adminSubscribedAt')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.map((sub) => (
                        <tr 
                          key={sub.id} 
                          className="border-b border-white/5 hover:bg-white/5"
                          data-testid={`subscriber-item-${sub.id}`}
                        >
                          <td className="p-4">
                            <a href={`mailto:${sub.email}`} className="text-blue-400 hover:underline">
                              {sub.email}
                            </a>
                          </td>
                          <td className="p-4 text-white/60 text-sm hidden sm:table-cell">
                            {formatDate(sub.subscribed_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboardPage;
